import { useHttpClient, useToast } from '@airship-platform/airship-patterns';
import { retryOnNotFound } from '@airship-platform/http-service';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { AppointmentProposalOptionProduct } from '../appointmentProposalOptionProducts';
import { HelloSignConfiguration } from '../proposalIntegrations';
import {
  AppointmentProposalOption,
  AppointmentProposalOptionCosts,
  AppointmentProposalOptionCreate,
  AppointmentProposalOptionUpdate,
} from './appointmentProposalOptions.models';
import {
  appointmentProposalOptionsReducer,
  AppointmentProposalOptionsState,
  initState,
} from './appointmentProposalOptions.reducer';

type CreatePayload = AppointmentProposalOptionCreate;
type UpdatePayload = AppointmentProposalOptionUpdate;

export type CreateDocumentSignaturePayload = Pick<
  HelloSignConfiguration,
  'templateId' | 'fileType' | 'externalIdName' | 'mergeFields' | 'customFields'
>;

const isReadOnly = (option: AppointmentProposalOption) =>
  !!option.acceptedDate ||
  option.status === 'SOLD' ||
  option.status === 'DELETED';

interface AppointmentsContextProps {
  options: AppointmentProposalOption[];
  currentOption: AppointmentProposalOption | undefined;
  currentOptionCosts: AppointmentProposalOptionCosts | undefined;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  fetchAppointmentProposalOptions: (
    appointmentId: string,
    appointmentProposalId: string,
    options?: { signal?: AbortSignal }
  ) => Promise<AppointmentProposalOption[]>;
  fetchAppointmentProposalOption: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string,
    options?: { signal?: AbortSignal }
  ) => Promise<AppointmentProposalOption | undefined>;
  createAppointmentProposalOption: (
    appointmentId: string,
    appointmentProposalId: string,
    create: CreatePayload
  ) => Promise<AppointmentProposalOption>;
  createAppointmentProposalOptions: (
    appointmentId: string,
    appointmentProposalId: string,
    create: CreatePayload[]
  ) => Promise<AppointmentProposalOption[]>;
  updateAppointmentProposalOption: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string,
    update: UpdatePayload
  ) => Promise<AppointmentProposalOption>;
  updateAppointmentProposalOptionEnrichment: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string
  ) => Promise<undefined | { products: AppointmentProposalOptionProduct[] }>;
  buildAppointmentProposalOptionDocumentSignature: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string,
    payload: CreateDocumentSignaturePayload
  ) => Promise<AppointmentProposalOption | undefined>;
  upsertOptions: (options: AppointmentProposalOption[]) => void;
  settleOptions: (
    options: AppointmentProposalOption[],
    opts?: { error?: string }
  ) => void;
  setOptionsMutating: () => void;
  selectProposalOptions: (appointmentProposalId: string) => void;
  selectOptionId: (
    appointmentProposalId: string,
    appointmentProposalOptionId: string
  ) => void;
  clearSelectedOption: () => void;
  updateCosts: (
    appointmentProposalOptionId: string,
    appointmentProposalOptionCosts: AppointmentProposalOptionCosts
  ) => void;
}

export const AppointmentProposalOptionsContext = createContext<
  AppointmentsContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<AppointmentProposalOptionsState>;
}>;

export const AppointmentProposalOptionsProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(appointmentProposalOptionsReducer, {
    ...initState,
    ...initialState,
  });
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    state.error && showToast(state.error, 'error');
  }, [state.error, showToast]);

  const options = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .filter(
          (a) =>
            a.appointmentProposalId === state.selectedProposalId &&
            a.status !== 'DELETED'
        )
        .map((a) => ({ ...a, isReadOnly: isReadOnly(a) }))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [state.entities, state.ids, state.selectedProposalId]
  );

  const currentOption = useMemo(() => {
    const option = state.selectedId && state.entities[state.selectedId];
    return option ? { ...option, isReadOnly: isReadOnly(option) } : undefined;
  }, [state.entities, state.selectedId]);

  const currentOptionCosts = useMemo(
    () => state.costBreakdown[state.selectedId || ''],
    [state.costBreakdown, state.selectedId]
  );

  const createAppointmentProposalOption: AppointmentsContextProps['createAppointmentProposalOption'] =
    useCallback(
      async (appointmentId, appointmentProposalId, create) => {
        dispatch({ type: 'CREATE_APPOINTMENT_PROPOSAL_OPTION' });
        try {
          const appointmentProposalOption =
            await httpClient.post<AppointmentProposalOption>(
              `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options`,
              create
            );
          dispatch({
            type: 'CREATE_APPOINTMENT_PROPOSAL_OPTION_SUCCESS',
            payload: { appointmentProposalOption },
          });
          return appointmentProposalOption;
        } catch (error) {
          dispatch({
            type: 'CREATE_APPOINTMENT_PROPOSAL_OPTION_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const createAppointmentProposalOptions: AppointmentsContextProps['createAppointmentProposalOptions'] =
    useCallback(
      async (appointmentId, appointmentProposalId, create) => {
        dispatch({ type: 'CREATE_APPOINTMENT_PROPOSAL_OPTIONS' });
        try {
          const appointmentProposalOptions = await Promise.all(
            create.map((payload) =>
              httpClient.post<AppointmentProposalOption>(
                `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options`,
                payload
              )
            )
          );
          dispatch({
            type: 'CREATE_APPOINTMENT_PROPOSAL_OPTIONS_SUCCESS',
            payload: { appointmentProposalOptions },
          });
          return appointmentProposalOptions;
        } catch (error) {
          dispatch({
            type: 'CREATE_APPOINTMENT_PROPOSAL_OPTIONS_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const fetchAppointmentProposalOptions: AppointmentsContextProps['fetchAppointmentProposalOptions'] =
    useCallback(
      async (appointmentId, appointmentProposalId, options) => {
        dispatch({
          type: 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS',
          payload: { appointmentProposalId },
        });
        try {
          const appointmentProposalOptions = await httpClient.get<
            AppointmentProposalOption[]
          >(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options`,
            { signal: options?.signal }
          );
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS_SUCCESS',
            payload: { appointmentProposalOptions, appointmentProposalId },
          });
          return appointmentProposalOptions;
        } catch (error) {
          if (error instanceof Error && error.message === 'canceled') {
            dispatch({ type: 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS_CANCELLED' });
            return [];
          }
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS_FAILURE',
            payload: { error: `${error}` },
          });
          return [];
        }
      },
      [httpClient]
    );

  const fetchAppointmentProposalOptionInternal = useCallback(
    async (
      appointmentId: string,
      appointmentProposalId: string,
      appointmentProposalOptionId: string,
      signal?: AbortSignal
    ) =>
      httpClient.get<AppointmentProposalOption>(
        `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}`,
        { signal }
      ),
    [httpClient]
  );

  const fetchAppointmentProposalOption: AppointmentsContextProps['fetchAppointmentProposalOption'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        options
      ) => {
        dispatch({
          type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION',
          payload: { appointmentProposalId, appointmentProposalOptionId },
        });

        try {
          const appointmentProposalOption =
            await fetchAppointmentProposalOptionInternal(
              appointmentId,
              appointmentProposalId,
              appointmentProposalOptionId,
              options?.signal
            );

          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION_SUCCESS',
            payload: { appointmentProposalOption },
          });

          return appointmentProposalOption;
        } catch (error) {
          if (error instanceof Error && error.message === 'canceled') {
            dispatch({ type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION_CANCELLED' });
            return;
          }
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION_FAILURE',
            payload: { error: `${error}` },
          });
        }
      },
      [fetchAppointmentProposalOptionInternal]
    );

  const updateAppointmentProposalOption: AppointmentsContextProps['updateAppointmentProposalOption'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        update
      ) => {
        dispatch({
          type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION',
          payload: {
            appointmentProposalId,
            appointmentProposalOptionId,
            appointmentProposalOption: update,
          },
        });

        try {
          const appointmentProposalOption =
            await httpClient.put<AppointmentProposalOption>(
              `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}`,
              update
            );
          dispatch({
            type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_SUCCESS',
            payload: { appointmentProposalOption },
          });

          return appointmentProposalOption;
        } catch (error) {
          dispatch({
            type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const updateAppointmentProposalOptionEnrichment: AppointmentsContextProps['updateAppointmentProposalOptionEnrichment'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId
      ) => {
        dispatch({
          type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_ENRICHMENT',
        });

        try {
          const { costs, products } = await retryOnNotFound(() =>
            httpClient.post<{
              costs: AppointmentProposalOptionCosts;
              products: AppointmentProposalOptionProduct[];
            }>(
              `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/enrichment`,
              {}
            )
          );

          dispatch({
            type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_ENRICHMENT_SUCCESS',
            payload: {
              appointmentProposalOptionId,
              appointmentProposalOptionCosts: costs,
            },
          });

          return { products };
        } catch {
          dispatch({
            type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_ENRICHMENT_FAILURE',
            payload: {
              error:
                'We were unable to update the option pricing. Please try again.',
            },
          });
        }
      },
      [httpClient]
    );

  const buildAppointmentProposalOptionDocumentSignature: AppointmentsContextProps['buildAppointmentProposalOptionDocumentSignature'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        payload
      ) => {
        dispatch({ type: 'GENERATE_DOCUMENT_SIGNATURE' });
        try {
          const url = `/proposal-integrations/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/sign`;
          const appointmentProposalOption =
            await httpClient.post<AppointmentProposalOption>(url, payload);

          dispatch({
            type: 'GENERATE_DOCUMENT_SIGNATURE_SUCCESS',
            payload: { appointmentProposalOption },
          });
          return appointmentProposalOption;
        } catch (error) {
          dispatch({
            type: 'GENERATE_DOCUMENT_SIGNATURE_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const upsertOptions: AppointmentsContextProps['upsertOptions'] = useCallback(
    (opts) =>
      dispatch({
        type: 'UPSERT_OPTIONS',
        payload: { appointmentProposalOptions: opts },
      }),
    []
  );

  const settleOptions: AppointmentsContextProps['settleOptions'] = useCallback(
    (opts, payload = {}) =>
      dispatch({
        type: 'SETTLE_OPTIONS',
        payload: { appointmentProposalOptions: opts, ...payload },
      }),
    []
  );

  const selectProposalOptions: AppointmentsContextProps['selectProposalOptions'] =
    useCallback(
      (appointmentProposalId) =>
        dispatch({
          type: 'SELECT_PROPOSAL_OPTIONS',
          payload: { appointmentProposalId },
        }),
      []
    );

  const selectOptionId: AppointmentsContextProps['selectOptionId'] =
    useCallback(
      (appointmentProposalId, appointmentProposalOptionId) =>
        dispatch({
          type: 'SELECT_OPTION_ID',
          payload: { appointmentProposalId, appointmentProposalOptionId },
        }),
      []
    );

  const clearSelectedOption: AppointmentsContextProps['clearSelectedOption'] =
    useCallback(() => dispatch({ type: 'CLEAR_SELECTED_OPTION' }), []);

  const updateCosts: AppointmentsContextProps['updateCosts'] = useCallback(
    (appointmentProposalOptionId, appointmentProposalOptionCosts) =>
      dispatch({
        type: 'UPDATE_OPTION_COSTS',
        payload: {
          appointmentProposalOptionId,
          appointmentProposalOptionCosts,
        },
      }),
    []
  );

  const setOptionsMutating: AppointmentsContextProps['setOptionsMutating'] =
    useCallback(() => dispatch({ type: 'SET_OPTIONS_MUTATING' }), []);

  return (
    <AppointmentProposalOptionsContext.Provider
      value={{
        fetchAppointmentProposalOptions,
        fetchAppointmentProposalOption,
        createAppointmentProposalOption,
        createAppointmentProposalOptions,
        updateAppointmentProposalOption,
        updateAppointmentProposalOptionEnrichment,
        buildAppointmentProposalOptionDocumentSignature,
        upsertOptions,
        settleOptions,
        setOptionsMutating,
        selectProposalOptions,
        selectOptionId,
        clearSelectedOption,
        updateCosts,
        options,
        currentOption,
        currentOptionCosts,
        error: state.error,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
      }}
    >
      {children}
    </AppointmentProposalOptionsContext.Provider>
  );
};

export const useAppointmentProposalOptions = () => {
  const context = useContext(AppointmentProposalOptionsContext);
  if (!context) {
    throw new Error(
      'useAppointmentProposalOptions must be used within an AppointmentProposalOptionsProvider'
    );
  }
  return context;
};
